import { vec3 } from 'gl-matrix';
import { StructureSdf } from '~/domain/structures/structureSdf';
import { Grid } from '~/engine/data/common/Grid';
import { SDF_ISO_THRESHOLD } from '~/engine/util/algorithms/sdf/sdfConstants';
import { quantile } from '~/utils/number';

/**
 * How much of a slice's widest row a row must match to count as body
 * rather than as something that merely came along with it.
 *
 * A strap, a table accessory or a speck of noise below the patient would
 * otherwise set the couch top, because this looks for the lowest point and
 * they are lower.
 */
const MIN_ROW_WIDTH_FRACTION = 0.2;

/**
 * Quantile of the per-slice lowest body point taken as the couch top.
 *
 * Not the maximum: one bad slice — a field that leaked onto the table, a
 * slice through an immobilisation base — would drag the answer down with
 * nothing to balance it. Not the median either: the patient does not touch
 * the couch on every slice (a head on a rest, a raised knee), and those
 * slices sit well ABOVE the couch, so a central statistic reads high.
 * A high quantile is the lowest the body reliably reaches, which is where
 * it rests.
 */
const CONTACT_QUANTILE = 0.9;

/**
 * The couch top, as a continuous voxel Y in `imageGrid`, inferred from
 * where the patient rests on it — or null when the field holds nothing.
 *
 * The patient lies ON the couch, so the surface the body reliably reaches
 * down to IS the couch top, to within how far the pad compresses under them. That
 * is an estimate to drag from, never a measurement: it reads the patient,
 * not the couch, and a thick pad or a prone setup on a headrest moves the
 * two apart.
 */
export function couchTopFromBody(
    body: StructureSdf,
    imageGrid: Grid
): number | null {
    const { pixels, grid } = body;
    const { sizeX, sizeY } = grid;
    // Field voxel -> image voxel Y is affine, so three basis differences
    // describe it exactly and no per-voxel transform is needed. Taken
    // through patient space, which is the only frame the two grids share:
    // a contour texture may be cropped, rescaled AND rotated relative to
    // the image it belongs to.
    const imageVoxelY = (v: vec3) =>
        imageGrid.toVoxelSpace(grid.toWorldSpace(v))[1];
    const origin = imageVoxelY(vec3.fromValues(0, 0, 0));
    const perX = imageVoxelY(vec3.fromValues(1, 0, 0)) - origin;
    const perY = imageVoxelY(vec3.fromValues(0, 1, 0)) - origin;
    const perZ = imageVoxelY(vec3.fromValues(0, 0, 1)) - origin;
    // A field index addresses a voxel's NEAR corner, but a texel at or above
    // the iso-value means the voxel's CENTRE is inside — so the body's lower
    // surface lies at the FAR edge of the last inside voxel, not at its index.
    // The far edge is whichever corner of the voxel box each axis pushes
    // deepest, which is sign-agnostic and so survives a flipped or rotated
    // field. Added once to the answer: it is the same for every voxel, and
    // shifting them all alike changes neither the row filter nor the
    // quantile's choice.
    const toFarEdge = Math.max(perX, 0) + Math.max(perY, 0) + Math.max(perZ, 0);

    const lowestPerSlice: number[] = [];
    for (let z = 0; z < pixels.length; z++) {
        const slice = pixels[z];
        if (!slice) {
            continue;
        }
        // One pass per slice: how many voxels each row holds, and the
        // lowest image row any of them reaches.
        const counts = new Int32Array(sizeY);
        const lowest = new Float64Array(sizeY).fill(-Infinity);
        for (let y = 0; y < sizeY; y++) {
            const row = y * sizeX;
            let count = 0;
            let deepest = -Infinity;
            for (let x = 0; x < sizeX; x++) {
                if (slice[row + x] >= SDF_ISO_THRESHOLD) {
                    count++;
                    const at = origin + perX * x + perY * y + perZ * z;
                    if (at > deepest) {
                        deepest = at;
                    }
                }
            }
            counts[y] = count;
            lowest[y] = deepest;
        }

        const widest = Math.max(...counts);
        if (widest === 0) {
            continue;
        }
        const needed = widest * MIN_ROW_WIDTH_FRACTION;
        let sliceLowest = -Infinity;
        for (let y = 0; y < sizeY; y++) {
            if (counts[y] >= needed && lowest[y] > sliceLowest) {
                sliceLowest = lowest[y];
            }
        }
        if (Number.isFinite(sliceLowest)) {
            lowestPerSlice.push(sliceLowest);
        }
    }

    if (lowestPerSlice.length === 0) {
        return null;
    }
    return quantile(lowestPerSlice, CONTACT_QUANTILE) + toFarEdge;
}
